import React, { FunctionComponent } from 'react'
import { Divider, Grid, Heading, Text, usePercent } from '@afterpaytouch/core'
import { Money } from '../Money'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { useAppRouter, useIsConsumerLendingOrder, useSelectedPaymentOption } from '../../state'
import styles from './style.module.css'

/**
 * "Summary" section of the redesigned Pay Monthly summary page (CASHDAP-3071, project: Dynamic
 * checkout unification 2026). Groups the regulated cost-of-credit disclosure into a dedicated
 * Summary block — purchase amount, interest (with APR), total amount payable, and amount due today
 * — matching the Pay in 4 / Bronte breakdown treatment. Copy + ordering per the FEB review slides
 * and Figma SOT frame 726:20432.
 *
 * Replaces the inline APR/interest/total list previously rendered by `ConsumerLendingPlanDetails`;
 * used only by the redesigned page (`summary-pay-monthly-redesign`). All figures come from the
 * selected `PaymentOption`.
 */
export const ConsumerLendingSummaryBreakdown: FunctionComponent = () => {
  const { t } = useTranslation()
  const { sectionHeadingSize, textSize } = useCheckoutSizes()
  const selectedPaymentOption = useSelectedPaymentOption()
  const isConsumerLending = useIsConsumerLendingOrder()
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })

  if (selectedPaymentOption === undefined || !isConsumerLending) {
    return null
  }

  const { principalAmount, interestAmount, totalAmount, amountDueToday, apr } = selectedPaymentOption

  return (
    <>
      <div className={styles.heading}>
        <Heading size={sectionHeadingSize} renderAs='h2'>
          {t('consumerLending:summary:breakdownTitle')}
        </Heading>
      </div>
      {/* Cost-of-credit lines (purchase + interest), grouped tight. The wrapping div breaks the
          design-system `.grid + .grid` adjacency so this group is visually separate from the totals
          below (matching the approved design + PI4's divider-separated breakdown). */}
      <div className={styles.amountsGroup}>
        <Grid>
          <Text size={textSize} renderAs='span'>
            {t('consumerLending:summary:purchaseAmount')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-purchaseAmount'>
            <Money value={principalAmount} size={textSize} />
          </Grid.Item>
        </Grid>
        {interestAmount !== null && (
          <Grid>
            <Text size={textSize} renderAs='span'>
              {t('consumerLending:summary:interestWithApr', { apr: formatPercent(apr ?? 0) })}
            </Text>
            <Grid.Item sm='auto' data-testid='clSummaryBreakdown-interestAmount'>
              <Money value={interestAmount} size={textSize} />
            </Grid.Item>
          </Grid>
        )}
      </div>
      <div className={styles.groupDivider}>
        {/* margin='No' so the only spacing is the wrapper's .groupDivider (Divider defaults to
            'Regular' = 16px each side, which would stack to ~28px). */}
        <Divider kind='Hairline' margin='No' />
      </div>
      {/* Totals (total payable + due today), grouped tight. */}
      <div className={styles.amountsGroup}>
        <Grid>
          <Text size={textSize} renderAs='span'>
            {t('consumerLending:summary:totalAmountPayable')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-totalAmount'>
            <Money value={totalAmount} size={textSize} />
          </Grid.Item>
        </Grid>
        <Grid>
          <Text size={textSize} renderAs='span' bold>
            {t('consumerLending:summary:dueToday')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-amountDueToday'>
            <Money value={amountDueToday} size={textSize} bold />
          </Grid.Item>
        </Grid>
      </div>
    </>
  )
}
